// client/src/components/pages/LexiStar/NotificationRules/Budgets.jsx
//
// Channel budgets (FR-NTF-009; LS-09 §5.23): per paid channel and month, the
// budget, a soft cap (warning) and a hard cap, what happens at the cap
// (fall back to a free channel, critical only, or block), and the spend so
// far this month from the cost ledger.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import { inputClasses, cardClasses } from '../StatusBoard/atoms';
import { fmtZar } from '../Messages/messageMeta';
import { getBudgets, updateBudgets, getCostLedger, pickRows } from './notificationsApi';
import { channelName } from './ruleLogic';

const CHANNELS = ['whatsapp', 'sms', 'telegram'];
const AT_CAP = [
  { value: 'fallback_free', label: 'Fall back to a free channel' },
  { value: 'critical_only', label: 'Critical messages only' },
  { value: 'block', label: 'Stop sending' },
];

const monthString = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
export const shiftMonth = (m, delta) => {
  const [y, mo] = m.split('-').map(Number);
  return monthString(new Date(y, mo - 1 + delta, 1));
};
const monthLabel = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/** { channel → amount_zar } from whichever spend shape the API returns. */
export function spendByChannel(res) {
  const rows = pickRows(res, 'spend', 'spend_this_month', 'channel_spend_month', 'totals');
  const out = {};
  rows.forEach((r) => {
    if (!r.channel) return;
    out[r.channel] =
      (out[r.channel] || 0) + (Number(r.amount_zar ?? r.spend_zar ?? r.spent_zar) || 0);
  });
  pickRows(res, 'budgets').forEach((b) => {
    const v = b.spend_zar ?? b.spent_zar ?? b.spend_this_month_zar;
    if (v !== undefined && v !== null && out[b.channel] === undefined)
      out[b.channel] = Number(v) || 0;
  });
  return out;
}

function Meter({ spent, budget, soft, hard }) {
  const max = Math.max(hard || 0, budget || 0, soft || 0, spent || 0) || 1;
  const pct = (v) => `${Math.min(100, (v / max) * 100)}%`;
  const over = hard != null && spent >= hard;
  const warn = !over && soft != null && spent >= soft;
  return (
    <div className="relative h-2.5 rounded-full bg-slate-100 overflow-visible" aria-hidden="true">
      <div
        className={`h-full rounded-full ${over ? 'bg-red-600' : warn ? 'bg-amber-500' : 'bg-slate-700'}`}
        style={{ width: pct(spent || 0) }}
      />
      {soft != null && (
        <span
          className="absolute -top-1 -bottom-1 w-0.5 bg-amber-500"
          style={{ left: pct(soft) }}
          title="Soft cap"
        />
      )}
      {hard != null && (
        <span
          className="absolute -top-1 -bottom-1 w-0.5 bg-red-600"
          style={{ left: pct(hard) }}
          title="Hard cap"
        />
      )}
    </div>
  );
}

export default function Budgets({ canManage }) {
  const [month, setMonth] = useState(() => monthString(new Date()));
  const [rows, setRows] = useState({});
  const [spend, setSpend] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const res = await getBudgets({ month });
      const byChannel = {};
      pickRows(res, 'budgets').forEach((b) => (byChannel[b.channel] = b));
      setRows(
        Object.fromEntries(
          CHANNELS.map((c) => [
            c,
            {
              budget_zar: byChannel[c]?.budget_zar ?? '',
              soft_cap_zar: byChannel[c]?.soft_cap_zar ?? '',
              hard_cap_zar: byChannel[c]?.hard_cap_zar ?? '',
              at_cap: byChannel[c]?.at_cap || 'fallback_free',
            },
          ])
        )
      );
      let s = spendByChannel(res);
      if (!Object.keys(s).length) {
        s = await getCostLedger({ month, limit: 1 })
          .then(spendByChannel)
          .catch(() => ({}));
      }
      setSpend(s);
    } catch (err) {
      setMessage({ ok: false, text: err.response?.data?.error || 'Failed to load the budgets.' });
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (c, patch) => setRows((r) => ({ ...r, [c]: { ...r[c], ...patch } }));
  const invalid = useMemo(
    () =>
      CHANNELS.some((c) => {
        const r = rows[c] || {};
        const s = num(r.soft_cap_zar);
        const h = num(r.hard_cap_zar);
        return (
          [r.budget_zar, r.soft_cap_zar, r.hard_cap_zar].some(
            (v) => v !== '' && v !== null && !(Number(v) >= 0)
          ) ||
          (s != null && h != null && s > h)
        );
      }),
    [rows]
  );

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await updateBudgets({
        month,
        budgets: CHANNELS.map((c) => ({
          channel: c,
          budget_zar: num(rows[c].budget_zar),
          soft_cap_zar: num(rows[c].soft_cap_zar),
          hard_cap_zar: num(rows[c].hard_cap_zar),
          at_cap: rows[c].at_cap,
        })),
      });
      setMessage({ ok: true, text: `Budgets for ${monthLabel(month)} saved.` });
      load();
    } catch (err) {
      setMessage({ ok: false, text: err.response?.data?.error || 'The budgets were not saved.' });
    } finally {
      setSaving(false);
    }
  };

  const money = (c, field, label) => (
    <div>
      <label
        htmlFor={`budget-${c}-${field}`}
        className="block text-[11px] font-medium text-slate-500 mb-0.5"
      >
        {label} (R)
      </label>
      <input
        id={`budget-${c}-${field}`}
        type="number"
        min="0"
        step="10"
        value={rows[c]?.[field] ?? ''}
        onChange={(e) => set(c, { [field]: e.target.value })}
        className={inputClasses}
        disabled={!canManage}
      />
    </div>
  );

  return (
    <section className={cardClasses} aria-label="Channel budgets">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
          aria-label="Previous month"
          className="p-2 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
        >
          <FaChevronLeft size={10} />
        </button>
        <span className="min-w-[9rem] text-center text-sm font-semibold text-slate-800">
          {monthLabel(month)}
        </span>
        <button
          type="button"
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
          aria-label="Next month"
          className="p-2 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
        >
          <FaChevronRight size={10} />
        </button>
        <p className="ml-auto text-xs text-slate-500">In-app is free and has no budget.</p>
      </div>
      {loading ? (
        <p className="px-4 py-6 text-sm text-slate-500">Loading budgets...</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {CHANNELS.map((c) => {
            const r = rows[c] || {};
            const spent = spend[c] || 0;
            return (
              <li
                key={c}
                className="px-4 py-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-end"
                aria-label={`${channelName(c)} budget`}
              >
                <div className="lg:col-span-3 space-y-1.5">
                  <p className="font-semibold text-slate-900">{channelName(c)}</p>
                  <p className="text-sm text-slate-700 tabular-nums">
                    {fmtZar(spent)} spent
                    {num(r.budget_zar) != null ? ` of ${fmtZar(r.budget_zar)}` : ''}
                  </p>
                  <Meter
                    spent={spent}
                    budget={num(r.budget_zar)}
                    soft={num(r.soft_cap_zar)}
                    hard={num(r.hard_cap_zar)}
                  />
                </div>
                <div className="lg:col-span-2">{money(c, 'budget_zar', 'Budget')}</div>
                <div className="lg:col-span-2">{money(c, 'soft_cap_zar', 'Soft cap')}</div>
                <div className="lg:col-span-2">{money(c, 'hard_cap_zar', 'Hard cap')}</div>
                <div className="lg:col-span-3">
                  <label
                    htmlFor={`budget-${c}-atcap`}
                    className="block text-[11px] font-medium text-slate-500 mb-0.5"
                  >
                    At the hard cap
                  </label>
                  <ThemedSelect
                    id={`budget-${c}-atcap`}
                    value={r.at_cap}
                    onChange={(e) => set(c, { at_cap: e.target.value })}
                    className={inputClasses}
                    disabled={!canManage}
                  >
                    {AT_CAP.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </ThemedSelect>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-3">
        <p role="status" className={`text-sm ${message?.ok ? 'text-emerald-700' : 'text-red-700'}`}>
          {message?.text ||
            (invalid ? 'Amounts must be 0 or more, and the soft cap at most the hard cap.' : '')}
        </p>
        {canManage && (
          <button
            type="button"
            onClick={save}
            disabled={saving || invalid || loading}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-slate-400"
          >
            {saving ? 'Saving...' : 'Save budgets'}
          </button>
        )}
      </div>
    </section>
  );
}
